import React, { useMemo } from 'react';

/**
 * MiniSparkline
 * - Lightweight inline SVG sparkline with average label.
 * - No external chart libraries to keep bundle size small.
 */
const MiniSparkline = ({
  data = [],
  width = 200,
  height = 80,
  strokeColor = '#06b6d4', // cyan-500
  fillColor = 'rgba(6,182,212,0.12)', // cyan-500 @ 12%
  label = 'Avg',
  valueFormatter, // optional: (number) => string, e.g., v => `$${v.toFixed(1)}K`
  showAvgLine = true,
  yDomain, // optional: [min, max]
  statValue, // optional: overrides displayed stat (defaults to avg of data)
  statValueFormatter, // optional: formatter specifically for statValue
  hideCountFooter = false, // optional: when true, hides the default `{data.length} assets` footer
  footerText, // optional: custom footer text
}) => {
  const {pathD, areaD, avg, min, max, avgY, formatted } = useMemo(() => {
    const vals = (Array.isArray(data) ? data : [])
      .map((v) => (typeof v === 'number' ? v : Number(v)))
      .filter((v) => Number.isFinite(v));

    const n = vals.length;
    if (n === 0) {
      const fmt = (valueFormatter
        ? (v) => valueFormatter(Number(v) || 0)
        : (v) => {
            const num = Number(v) || 0;
            return `${num.toFixed(1)}`;
          }
      );
      return { points: [], pathD: '', areaD: '', avg: null, min: null, max: null, avgY: null, formatted: { fmt } };
    }

    const minV = (typeof yDomain?.[0] === 'number') ? yDomain[0] : Math.min(...vals);
    const maxV = (typeof yDomain?.[1] === 'number') ? yDomain[1] : Math.max(...vals);
    const avgV = vals.reduce((s, v) => s + v, 0) / n;

    const padX = 6; // inner padding
    const padY = 8;
    const innerW = Math.max(1, width - padX * 2);
    const innerH = Math.max(1, height - padY * 2);

    const xFor = (i) => (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW) + padX;
    const yFor = (v) => {
      // map value (minV..maxV) to y (inverted)
      const range = maxV === minV ? 1 : (maxV - minV);
      const t = (v - minV) / range; // 0..1
      return height - padY - t * innerH;
    };

    const pts = vals.map((v, i) => [xFor(i), yFor(v)]);
    const path = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x},${y}`).join(' ');

    // Build area under the line to baseline
    let area = '';
    if (pts.length > 1) {
      area = `M${pts[0][0]},${pts[0][1]} ` +
             pts.slice(1).map(([x, y]) => `L${x},${y}`).join(' ') +
             ` L${pts[pts.length - 1][0]},${height - padY}` +
             ` L${pts[0][0]},${height - padY} Z`;
    }

    // Choose default formatter: percent if values look like 0..100, otherwise raw number
    const looksLikePercent = vals.every((v) => v >= 0 && v <= 100);
    const defaultFormatter = (v) => {
      const num = Number(v) || 0;
      return looksLikePercent ? `${num.toFixed(1)}%` : `${num.toFixed(1)}`;
    };
    const fmt = valueFormatter || defaultFormatter;

    return { points: pts, pathD: path, areaD: area, avg: avgV, min: minV, max: maxV, avgY: yFor(avgV), formatted: { fmt } };
  }, [data, width, height, valueFormatter, yDomain]);

  const useStat = (typeof statValue === 'number' && Number.isFinite(statValue));
  const displayStat = useStat ? (statValueFormatter ? statValueFormatter(statValue) : (valueFormatter ? valueFormatter(statValue) : formatted.fmt(statValue))) : (Number.isFinite(avg) ? formatted.fmt(avg) : '—');

  const titleText = (data && data.length)
    ? `${label}: ${displayStat}${Number.isFinite(min) && Number.isFinite(max) ? ` (min ${formatted.fmt(min)}, max ${formatted.fmt(max)})` : ''}`
    : 'No data';

  return (
    <div
      className="flex items-center gap-3 p-2 pr-3 bg-white rounded-lg border border-gray-200 shadow-sm"
      aria-label="Mini chart"
      title={titleText}
      style={{ maxWidth: width + 110 }}
      data-testid="mini-sparkline"
    >
      <svg width={width} height={height} className="block">
        {/* Background grid baseline */}
        <line x1="0" y1={height - 8} x2={width} y2={height - 8} stroke="#e5e7eb" strokeWidth="1" />
        {/* Area fill */}
        {areaD && (
          <path d={areaD} fill={fillColor} stroke="none" />
        )}
        {/* Line stroke */}
        {pathD && (
          <path d={pathD} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        )}
        {/* Average marker */}
        {showAvgLine && Number.isFinite(avg) && Number.isFinite(avgY) && (
          <g>
            <line x1="0" x2={width} y1={avgY} y2={avgY} stroke="#94a3b8" strokeDasharray="3,3" />
          </g>
        )}
      </svg>
      <div className="min-w-[96px]">
        <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
        <div className="text-sm font-semibold text-gray-800">
          {displayStat}
        </div>
        {!hideCountFooter && (
          <div className="text-[10px] text-gray-500">
            {typeof footerText === 'string' ? footerText : (data && data.length ? `${data.length} assets` : 'No data')}
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniSparkline;
